/**
 * ChangeDrugStatusTableSelect Component
 *
 * A dropdown component used to change the status of a drug directly from a table row.
 * Displays the current status, allows selection from other available statuses,
 * and updates the status via an API call.
 *
 * Props:
 * @param {Record<string, string>} variantEnum - An object containing the possible drug statuses.
 * @param {string | undefined} selectedOption - Currently selected status.
 * @param {any} rowDrug - The drug object corresponding to the table row.
 *
 * Behavior:
 * - Displays the current drug status with a background color variant.
 * - Clicking the button toggles a dropdown with other available statuses.
 * - Selecting a status triggers an API call to update the drug status.
 * - Shows a loader while the API call is pending.
 * - Updates the UI optimistically if the API call succeeds.
 * - Handles clicks outside the dropdown to automatically close it.
 * - Dynamically positions the dropdown above or below the button depending on available space.
 * - Displays an error message if the API call fails.
 *
 * Usage:
 * ```tsx
 * <ChangeDrugStatusTableSelect
 *   variantEnum={DrugStatusEnum}
 *   selectedOption={drug.Status}
 *   rowDrug={drug}
 * />
 * ```
 *
 * Notes:
 * - Uses React Portal to render the dropdown outside the main DOM hierarchy.
 * - TailwindCSS is used for styling.
 * - Relies on Redux to get the current user's token for API calls.
 * - Can be extended to trigger additional callbacks on status change.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { TableSelectProps } from "../../types";
import { useChangeDrugStauts } from "../../utils/api/Drugs/useChangeDrugStatus";
import { DrugStatusEnum } from "../../utils/constants/CommonConstants";
import ErrorsWrapper from "../GeneralComponents/ErrorsWrapper";
import Loader from "../GeneralComponents/Loader";

const ChangeDrugStatusTableSelect: React.FC<TableSelectProps> = ({
  variantEnum,
  selectedOption,
  rowDrug,
}) => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentOption, setCurrentOption] = useState(selectedOption || "");

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const {
    mutate: ChangeDrugStautsMutate,
    status: ChangeDrugStautsStatus,
    error: ChangeDrugStautsError,
  } = useChangeDrugStauts();

  const getVariant = useCallback((value: string) => {
    switch (value) {
      case DrugStatusEnum.available:
        return "bg-primaryLighter !text-primaryMain";
      case DrugStatusEnum.unavailable:
        return "bg-warningLighter !text-warningMain";
      case DrugStatusEnum.hide:
        return "bg-hideOptionColor !text-textSecondary";
      default:
        return "bg-gray-300 text-black";
    }
  }, []);

  const handleOptionClick = (option: string) => {
    setCurrentOption(option);
    if (option) {
      ChangeDrugStautsMutate(
        {
          payload: {
            DrugId: rowDrug?.Id,
            StatusPayload: {
              Status: option,
            },
          },
          token: user?.token,
        },
        {
          onSuccess: (response) => {
            if (response.Status) {
              setCurrentOption(option);
              rowDrug.Status = option;
            }
          },
        },
      );
    }
    setDropdownOpen(false);
  };

  const otherOptions = Object.values(variantEnum);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Sync with prop
  useEffect(() => {
    if (rowDrug?.Status && rowDrug.Status !== currentOption) {
      setCurrentOption(rowDrug.Status);
    }
  }, [rowDrug?.Status]);

  // Calculate dropdown position
  useLayoutEffect(() => {
    if (dropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const estimatedHeight = 160;
      const showAbove = window.innerHeight - rect.bottom < estimatedHeight;

      setDropdownStyle({
        position: "fixed",
        top: showAbove ? rect.top - estimatedHeight - 8 : rect.bottom + 8,
        left: rect.left,
        zIndex: 9999,
      });
    }
  }, [dropdownOpen]);

  const dropdownContent = (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="page-animate z-[9999] w-fit min-w-[10rem] rounded-lg bg-white shadow"
    >
      <div className="clip-triangle absolute -top-2 right-4 h-6 w-6 bg-white"></div>

      {ChangeDrugStautsStatus === "pending" ? (
        <div className="flex flex-col items-center justify-center p-4">
          <Loader radius={50} />
        </div>
      ) : (
        <ul className="flex w-full flex-col gap-1 rounded-lg p-2 text-sm">
          {otherOptions.map((option) => {
            const isSelected = option === currentOption;
            return (
              <li
                key={option}
                onClick={() => handleOptionClick(option)}
                className={`cursor-pointer rounded-xl px-4 py-2 ${
                  isSelected ? "font-bold" : ""
                } ${getVariant(option)}`}
              >
                {t(option.charAt(0).toUpperCase() + option.slice(1))}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        className={`text-white ${getVariant(
          currentOption || "",
        )} inline-flex items-center rounded-lg px-5 py-2 text-sm font-medium`}
        type="button"
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        {currentOption
          ? t(currentOption.charAt(0).toUpperCase() + currentOption.slice(1))
          : t("select_status")}
        <svg
          className="ms-3 h-2.5 w-2.5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 10 6"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m1 1 4 4 4-4"
          />
        </svg>
      </button>

      {dropdownOpen &&
        typeof window !== "undefined" &&
        document.getElementById("portal-root") &&
        ReactDOM.createPortal(
          dropdownContent,
          document.getElementById("portal-root")!,
        )}

      {ChangeDrugStautsError && (
        <ErrorsWrapper message={ChangeDrugStautsError.message} />
      )}
    </>
  );
};

export default ChangeDrugStatusTableSelect;
